<script lang="ts">
  /**
   * Клітинка таблиці з редагуванням «по полю»: показує значення, при наведенні —
   * олівець, по кліку — плаваючий popover-редактор (як на kanban-картці).
   */
  import FieldDisplay from '../lib/fields/FieldDisplay.svelte';
  import FieldEditor from '../lib/fields/FieldEditor.svelte';
  import Icon from './Icon.svelte';
  import { showAlert } from '../lib/ui-alert';
  import type { ApiField } from '../lib/data-tables-api';

  let {
    field,
    value,
    record,
    editable = false,
    onSave,
    display,
  }: {
    field: ApiField;
    value: unknown;
    record?: Record<string, unknown>;
    editable?: boolean;
    onSave: (next: unknown) => Promise<void>;
    /** Кастомний рендер значення (напр. StatusBadge). Якщо нема — FieldDisplay. */
    display?: import('svelte').Snippet;
  } = $props();

  let open = $state(false);
  let draft = $state<unknown>(undefined);
  let busy = $state(false);
  let anchor = $state<{ x: number; y: number } | null>(null);

  const isEmpty = $derived(
    value == null || value === '' || (Array.isArray(value) && value.length === 0),
  );

  function startEdit(e: MouseEvent) {
    e.stopPropagation();
    const W = 300;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    anchor = {
      x: Math.min(Math.max(8, rect.left), window.innerWidth - W - 8),
      y: Math.min(rect.bottom + 6, window.innerHeight - 220),
    };
    draft = value;
    open = true;
  }
  function cancel() {
    open = false;
    draft = undefined;
  }
  async function commit() {
    if (busy) return;
    busy = true;
    try {
      await onSave(draft);
      open = false;
      draft = undefined;
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    } finally {
      busy = false;
    }
  }
  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && open) cancel();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<span class="group/cell flex items-center gap-1 w-full min-w-0">
  <span class="flex-1 min-w-0 truncate">
    {#if display}{@render display()}{:else if isEmpty}<span class="text-[var(--color-text-muted-2)]">—</span>{:else}<FieldDisplay {field} {value} {record} plainLinks />{/if}
  </span>
  {#if editable}
    <button
      type="button"
      title="Редагувати"
      onclick={startEdit}
      class="shrink-0 grid h-5 w-5 place-items-center rounded-full text-[var(--color-text-muted-2)] opacity-0 group-hover/cell:opacity-100 hover:bg-[var(--color-bg-3)] hover:text-[var(--color-pu)] transition-opacity"
    ><Icon name="edit" size={12} /></button>
  {/if}
</span>

{#if open && anchor}
  <button
    type="button"
    class="fixed inset-0 z-40 cursor-default"
    aria-label="Закрити"
    onclick={(e) => { e.stopPropagation(); cancel(); }}
  ></button>
  <div
    class="fixed z-50 w-[300px] rounded-xl border border-[var(--color-border-3)] bg-white shadow-2xl p-3.5"
    style="left: {anchor.x}px; top: {anchor.y}px;"
    role="dialog"
    onclick={(e) => e.stopPropagation()}
  >
    <div class="flex items-center gap-2 mb-2">
      <span class="text-[var(--color-text-muted-2)] inline-flex"><Icon name="edit" size={14} /></span>
      <span class="text-[12px] font-semibold">{field.label}</span>
    </div>
    <FieldEditor {field} value={draft} onChange={(v) => (draft = v)} />
    <div class="mt-3 flex justify-end gap-2">
      <button
        type="button"
        onclick={cancel}
        disabled={busy}
        class="rounded-lg border border-[var(--color-border-3)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide hover:bg-[var(--color-bg-3)]"
      >Скасувати</button>
      <button
        type="button"
        onclick={commit}
        disabled={busy}
        class="rounded-lg bg-[var(--color-pu)] text-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide hover:opacity-90 disabled:opacity-50"
      >{busy ? '…' : 'Зберегти'}</button>
    </div>
  </div>
{/if}
